import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Button, Paper, IconButton, Typography, Chip, TextField,
  InputAdornment, CircularProgress, Stack, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, alpha, Dialog, DialogActions, TablePagination,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Search as SearchIcon, CheckCircle as PostIcon, Undo as UnpostIcon,
  LocalPizza as BakeryIcon, Receipt as ReceiptIcon,
  PendingActions as PendingIcon, AccountBalance as AccountIcon,
  WarningAmber as WarningAmberIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  FilterAltOff as ClearFilterIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { toast } from 'react-toastify';
import { useLanguage } from '../contexts/LanguageContext';
import { useFiscalDateRange } from '../hooks/useFiscalDateRange';
import { branchIssuesService } from '../services/branchIssues.service';
import BranchIssueDialog from '../components/branchIssues/BranchIssueDialog';
import BranchIssuePreviewDialog from '../components/branchIssues/BranchIssuePreviewDialog';
import { BranchIssueDto } from '../types/branchIssues.types';

const t = {
  en: {
    title: 'Bakery Issues to Branches',
    subtitle: 'Bakery Sales — Internal Transfer',
    addNew: 'New Issue',
    issueNumber: 'Issue #', date: 'Date', branch: 'Branch',
    amount: 'Amount', status: 'Status', actions: 'Actions',
    search: 'Search...', noData: 'No issues found',
    draft: 'Draft', posted: 'Posted',
    confirmDeleteTitle: 'Delete Issue', confirmDeleteMsg: 'Are you sure you want to delete this issue? This action cannot be undone.',
    confirmPostTitle: 'Post Issue', confirmPostMsg: 'Post this issue? A journal entry will be created and stock will be updated.',
    confirmUnpostTitle: 'Unpost Issue', confirmUnpostMsg: 'Unpost this issue? The journal entry will be reversed and stock changes will be undone.',
    totalIssues: 'Total', draftCount: 'Draft', postedCount: 'Posted', totalAmount: 'Total Amount',
    journalEntry: 'Journal Entry',
    confirm: 'Confirm', cancel: 'Cancel',
  },
  ar: {
    title: 'إصدارات المخبز للفروع',
    subtitle: 'مبيعات المخبز الداخلية',
    addNew: 'إصدار جديد',
    issueNumber: 'رقم الإصدار', date: 'التاريخ', branch: 'الفرع المستلم',
    amount: 'المبلغ', status: 'الحالة', actions: 'الإجراءات',
    search: 'بحث...', noData: 'لا توجد إصدارات',
    draft: 'مسودة', posted: 'مرحّل',
    confirmDeleteTitle: 'حذف الإصدار', confirmDeleteMsg: 'هل أنت متأكد من حذف هذا الإصدار؟ لا يمكن التراجع عن هذا الإجراء.',
    confirmPostTitle: 'ترحيل الإصدار', confirmPostMsg: 'هل تريد ترحيل هذا الإصدار؟ سيتم إنشاء قيد محاسبي وتحديث المخزون.',
    confirmUnpostTitle: 'إلغاء ترحيل الإصدار', confirmUnpostMsg: 'هل تريد إلغاء ترحيل هذا الإصدار؟ سيتم حذف القيد المحاسبي وعكس التغييرات على المخزون.',
    totalIssues: 'الإجمالي', draftCount: 'مسودة', postedCount: 'مرحّل', totalAmount: 'إجمالي المبالغ',
    journalEntry: 'القيد',
    confirm: 'تأكيد', cancel: 'إلغاء',
  },
};

const ORANGE = '#e65100';

interface ConfirmState {
  open: boolean;
  title: string;
  message: string;
  color: string;
  onConfirm: () => void;
}

const BranchIssuesPage: React.FC = () => {
  const { language } = useLanguage();
  const tr = t[language as 'en' | 'ar'];
  const isRTL = language === 'ar';
  const { minDate, maxDate } = useFiscalDateRange();

  // Role-based permissions
  const userRoles = (() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return [] as string[];
      const payload = JSON.parse(atob(token.split('.')[1]));
      const roles = payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
      return roles ? (Array.isArray(roles) ? roles : [roles]) : [];
    } catch { return [] as string[]; }
  })();
  const editRoles = ['Super Admin', 'Admin', 'Warehouse Keeper', 'Warehouse Manager', 'Operations Manager', 'Accountant', 'Senior Accountant', 'Chief Accountant'];
  const canPost = (() => { try { const token = localStorage.getItem('token'); if (!token) return false; const payload = JSON.parse(atob(token.split('.')[1])); const perms = payload.Permission ? (Array.isArray(payload.Permission) ? payload.Permission : [payload.Permission]) : []; return perms.some((p: string) => p.endsWith('_POST')); } catch { return false; } })();
  const canEdit = userRoles.some(r => editRoles.includes(r));

  const [issues, setIssues] = useState<BranchIssueDto[]>([]);
  const [filtered, setFiltered] = useState<BranchIssueDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterBranch, setFilterBranch] = useState<string>('');
  const [filterDateFrom, setFilterDateFrom] = useState<string>('');
  const [filterDateTo, setFilterDateTo] = useState<string>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editIssue, setEditIssue] = useState<BranchIssueDto | null>(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewIssue, setPreviewIssue] = useState<BranchIssueDto | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState>({
    open: false, title: '', message: '', color: ORANGE, onConfirm: () => {},
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => { loadData(); }, []);

  useEffect(() => { setPage(0); }, [search, filterStatus, filterBranch, filterDateFrom, filterDateTo]);

  // Unique branches for the filter dropdown — derived from loaded issues so
  // we don't need a separate API call. Sorted alphabetically.
  const branchOptions = useMemo(() => {
    const map = new Map<number, string>();
    issues.forEach(i => { if (i.branchId && i.branchName) map.set(i.branchId, i.branchName); });
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [issues]);

  const paginated = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage]
  );

  // Auto-open branch issue preview when ?openId=X is in the URL (used by Inventory Rebalance trace)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const openId = params.get('openId');
    if (!openId) return;
    const id = parseInt(openId, 10);
    if (!Number.isFinite(id)) return;
    (async () => {
      try {
        const full = await branchIssuesService.getById(id);
        setPreviewIssue(full);
        setPreviewDialogOpen(true);
      } catch (err: any) {
        toast.error(err.response?.data?.message || err.message);
      }
    })();
  }, []);

  useEffect(() => {
    let result = issues;

    // Text search (issue number + branch name + request number)
    if (search.trim()) {
      const s = search.toLowerCase();
      result = result.filter(i =>
        i.issueNumber.toLowerCase().includes(s) ||
        (i.branchName || '').toLowerCase().includes(s) ||
        (i.requestNumber || '').toLowerCase().includes(s)
      );
    }

    // Status filter
    if (filterStatus) {
      result = result.filter(i => i.status === filterStatus);
    }

    // Branch filter
    if (filterBranch) {
      result = result.filter(i => String(i.branchId) === filterBranch);
    }

    // Date range — compare YYYY-MM-DD prefix so a stored "2026-06-28T00:00:00"
    // matches a filterDateTo "2026-06-28" boundary day (same fix as PI page).
    const dateOnly = (v?: string) => (v ? String(v).slice(0, 10) : '');
    if (filterDateFrom) {
      result = result.filter(i => dateOnly(i.issueDate) >= filterDateFrom);
    }
    if (filterDateTo) {
      result = result.filter(i => dateOnly(i.issueDate) <= filterDateTo);
    }

    setFiltered(result);
  }, [issues, search, filterStatus, filterBranch, filterDateFrom, filterDateTo]);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await branchIssuesService.getAll();
      setIssues([...data].sort((a, b) => b.branchIssueId - a.branchIssueId));
    } finally {
      setLoading(false);
    }
  };

  const closeConfirm = () => setConfirm(prev => ({ ...prev, open: false }));

  const handleAdd = () => { setEditIssue(null); setDialogOpen(true); };
  const handleEdit = (issue: BranchIssueDto) => { setEditIssue(issue); setDialogOpen(true); };

  const handleView = async (issue: BranchIssueDto) => {
    try {
      const full = await branchIssuesService.getById(issue.branchIssueId);
      setPreviewIssue(full);
      setPreviewDialogOpen(true);
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message);
    }
  };

  const handleDelete = (issue: BranchIssueDto) => {
    setConfirm({
      open: true,
      title: tr.confirmDeleteTitle,
      message: tr.confirmDeleteMsg,
      color: '#d32f2f',
      onConfirm: async () => {
        closeConfirm();
        try {
          await branchIssuesService.delete(issue.branchIssueId);
          toast.success(isRTL ? 'تم الحذف بنجاح' : 'Deleted successfully');
          await loadData();
        } catch (err: any) {
          toast.error(err.response?.data?.message || err.message);
        }
      },
    });
  };

  const handlePost = (issue: BranchIssueDto) => {
    setConfirm({
      open: true,
      title: tr.confirmPostTitle,
      message: tr.confirmPostMsg,
      color: '#2e7d32',
      onConfirm: async () => {
        closeConfirm();
        try {
          await branchIssuesService.post(issue.branchIssueId);
          toast.success(isRTL ? 'تم الترحيل وإنشاء القيد بنجاح' : 'Posted successfully');
          await loadData();
        } catch (err: any) {
          toast.error(err.response?.data?.message || err.message);
        }
      },
    });
  };

  const handleUnpost = (issue: BranchIssueDto) => {
    setConfirm({
      open: true,
      title: tr.confirmUnpostTitle,
      message: tr.confirmUnpostMsg,
      color: '#f57c00',
      onConfirm: async () => {
        closeConfirm();
        try {
          await branchIssuesService.unpost(issue.branchIssueId);
          toast.success(isRTL ? 'تم إلغاء الترحيل بنجاح' : 'Unposted successfully');
          await loadData();
        } catch (err: any) {
          toast.error(err.response?.data?.message || err.message);
        }
      },
    });
  };

  // Stats reflect the currently-filtered view so the top cards update as the
  // user narrows down — matches the behaviour of the Purchase Invoices page.
  const stats = useMemo(() => ({
    total: filtered.length,
    draft: filtered.filter(i => i.status === 'Draft').length,
    posted: filtered.filter(i => i.status === 'Posted').length,
    amount: filtered.reduce((s, i) => s + i.totalAmount, 0),
  }), [filtered]);

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>

      {/* Stats Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {[
          { label: tr.totalIssues, value: stats.total, icon: <ReceiptIcon />, color: ORANGE },
          { label: tr.draftCount, value: stats.draft, icon: <PendingIcon />, color: '#f57c00' },
          { label: tr.postedCount, value: stats.posted, icon: <PostIcon />, color: '#2e7d32' },
          { label: tr.totalAmount, value: `${stats.amount.toFixed(3)} KD`, icon: <AccountIcon />, color: '#1565c0' },
        ].map((s, i) => (
          <Card key={i} elevation={3} sx={{ flex: '1 1 200px', background: `linear-gradient(135deg, ${s.color} 0%, ${s.color}cc 100%)`, color: 'white' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h5" fontWeight="bold">{s.value}</Typography>
                  <Typography variant="body2">{s.label}</Typography>
                </Box>
                <Box sx={{ opacity: 0.3, fontSize: 48 }}>{s.icon}</Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Header */}
      <Paper elevation={3} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <BakeryIcon sx={{ color: ORANGE, fontSize: 32 }} />
            <Box>
              <Typography variant="h5" fontWeight="bold" color={ORANGE}>{tr.title}</Typography>
              <Typography variant="caption" color="text.secondary">{tr.subtitle}</Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Refresh">
              <IconButton onClick={loadData} color="primary"><RefreshIcon /></IconButton>
            </Tooltip>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}
              sx={{ bgcolor: ORANGE, '&:hover': { bgcolor: '#bf360c' } }}>
              {tr.addNew}
            </Button>
          </Stack>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder={tr.search} value={search} onChange={e => setSearch(e.target.value)}
            size="small" sx={{ minWidth: 240, flex: '1 1 240px' }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          />

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>{tr.status}</InputLabel>
            <Select value={filterStatus} label={tr.status} onChange={(e) => setFilterStatus(e.target.value)}>
              <MenuItem value="">{isRTL ? 'الكل' : 'All'}</MenuItem>
              <MenuItem value="Draft">{tr.draft}</MenuItem>
              <MenuItem value="Posted">{tr.posted}</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>{tr.branch}</InputLabel>
            <Select value={filterBranch} label={tr.branch} onChange={(e) => setFilterBranch(e.target.value)}>
              <MenuItem value="">{isRTL ? 'جميع الفروع' : 'All Branches'}</MenuItem>
              {branchOptions.map(([id, name]) => (
                <MenuItem key={id} value={String(id)}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={isRTL ? 'من تاريخ' : 'From Date'}
              value={filterDateFrom ? new Date(filterDateFrom) : null}
              onChange={(date) => { const v = date ? `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}` : ''; setFilterDateFrom(v); }}
              format="dd/MM/yyyy"
              minDate={minDate}
              maxDate={filterDateTo ? new Date(filterDateTo) : maxDate}
              views={['year', 'month', 'day']} openTo="day"
              slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
            />
            <DatePicker
              label={isRTL ? 'إلى تاريخ' : 'To Date'}
              value={filterDateTo ? new Date(filterDateTo) : null}
              onChange={(date) => { const v = date ? `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}` : ''; setFilterDateTo(v); }}
              format="dd/MM/yyyy"
              minDate={filterDateFrom ? new Date(filterDateFrom) : minDate}
              maxDate={maxDate}
              views={['year', 'month', 'day']} openTo="day"
              slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
            />
          </LocalizationProvider>

          {(search || filterStatus || filterBranch || filterDateFrom || filterDateTo) && (
            <Button size="small" startIcon={<ClearFilterIcon />}
              onClick={() => { setSearch(''); setFilterStatus(''); setFilterBranch(''); setFilterDateFrom(''); setFilterDateTo(''); }}>
              {isRTL ? 'مسح' : 'Clear'}
            </Button>
          )}
        </Box>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ background: `linear-gradient(135deg, ${ORANGE} 0%, #ff8f00 100%)` }}>
              {[tr.issueNumber, tr.date, tr.branch, tr.amount, tr.status, tr.journalEntry, tr.actions]
                .map(h => <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold' }}>{h}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress sx={{ color: ORANGE }} /></TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                <Typography color="text.secondary">{tr.noData}</Typography>
              </TableCell></TableRow>
            ) : paginated.map(issue => {
              const isDraft = issue.status === 'Draft';
              return (
                <TableRow key={issue.branchIssueId} hover sx={{ '&:hover': { bgcolor: alpha(ORANGE, 0.04) } }}>
                  <TableCell>
                    <Typography fontWeight="bold" color={ORANGE}>{issue.issueNumber}</Typography>
                    {issue.requestNumber && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {isRTL ? 'طلب:' : 'Req:'} {issue.requestNumber}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{new Date(issue.issueDate).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB')}</TableCell>
                  <TableCell>{issue.branchName}</TableCell>
                  <TableCell>
                    <Typography fontWeight="bold" color="success.main">{issue.totalAmount.toFixed(3)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={isDraft ? tr.draft : tr.posted}
                      color={isDraft ? 'warning' : 'success'}
                      size="small" sx={{ fontWeight: 'bold' }}
                    />
                  </TableCell>
                  <TableCell>
                    {issue.journalEntryId && (
                      <Chip label={`${issue.journalEntryNumber || 'JE'} (#${issue.journalEntryId})`} size="small" variant="outlined" color="primary" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title={isRTL ? 'معاينة' : 'Preview'}>
                        <IconButton size="small" sx={{ color: ORANGE }} onClick={() => handleView(issue)}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {isDraft && canEdit && (
                        <>
                          <Tooltip title={isRTL ? 'تعديل' : 'Edit'}>
                            <IconButton size="small" color="primary" onClick={() => handleEdit(issue)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={isRTL ? 'حذف' : 'Delete'}>
                            <IconButton size="small" color="error" onClick={() => handleDelete(issue)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {isDraft && canPost && (
                        <Tooltip title={isRTL ? 'ترحيل' : 'Post'}>
                          <IconButton size="small" color="success" onClick={() => handlePost(issue)}>
                            <PostIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!isDraft && canPost && (
                        <Tooltip title={isRTL ? 'إلغاء الترحيل' : 'Unpost'}>
                          <IconButton size="small" color="warning" onClick={() => handleUnpost(issue)}>
                            <UnpostIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {filtered.length > 0 && (
          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage={isRTL ? 'صفوف في الصفحة:' : 'Rows per page:'}
            labelDisplayedRows={({ from, to, count }) =>
              isRTL ? `${from}–${to} من ${count}` : `${from}–${to} of ${count}`
            }
            sx={{ direction: isRTL ? 'rtl' : 'ltr' }}
          />
        )}
      </TableContainer>

      {/* Confirm Dialog */}
      <Dialog open={confirm.open} onClose={closeConfirm} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }} dir={isRTL ? 'rtl' : 'ltr'}>
        <Box sx={{ textAlign: 'center', pt: 4, pb: 1, px: 3 }}>
          <Box sx={{
            width: 64, height: 64, borderRadius: '50%', mx: 'auto', mb: 2,
            background: `linear-gradient(135deg, ${confirm.color} 0%, ${confirm.color}cc 100%)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 4px 20px ${confirm.color}55`,
          }}>
            <WarningAmberIcon sx={{ fontSize: 34, color: '#fff' }} />
          </Box>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>{confirm.title}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{confirm.message}</Typography>
        </Box>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 2, px: 3 }}>
          <Button onClick={closeConfirm} variant="outlined"
            sx={{ borderRadius: 2, px: 4, textTransform: 'none', fontWeight: 600 }}>
            {tr.cancel}
          </Button>
          <Button onClick={confirm.onConfirm} variant="contained"
            sx={{
              borderRadius: 2, px: 4, textTransform: 'none', fontWeight: 600,
              background: `linear-gradient(135deg, ${confirm.color} 0%, ${confirm.color}cc 100%)`,
              boxShadow: `0 3px 12px ${confirm.color}55`,
              '&:hover': { filter: 'brightness(0.9)' },
            }}>
            {tr.confirm}
          </Button>
        </DialogActions>
      </Dialog>

      <BranchIssueDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={loadData}
        editIssue={editIssue}
      />

      <BranchIssuePreviewDialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        issue={previewIssue}
        onEdit={() => {
          setPreviewDialogOpen(false);
          if (previewIssue) handleEdit(previewIssue);
        }}
      />
    </Box>
  );
};

export default BranchIssuesPage;
